import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  api,
  type TreatmentsListParams,
  type CreateVariationInput,
} from '@/lib/api-client'
import { toast } from 'sonner'

/**
 * Hook to fetch paginated treatments list
 */
export function useTreatments(params?: TreatmentsListParams) {
  return useQuery({
    queryKey: ['treatments', params],
    queryFn: () => api.treatments.list(params),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook to fetch single treatment with details
 */
export function useTreatment(id: number | null) {
  return useQuery({
    queryKey: ['treatment', id],
    queryFn: () => api.treatments.get(id!),
    enabled: id !== null && id > 0,
    staleTime: 60 * 1000,
  })
}

/**
 * Hook to fetch treatment categories
 */
export function useTreatmentCategories() {
  return useQuery({
    queryKey: ['treatmentCategories'],
    queryFn: () => api.treatments.categories(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch treatment matching stats
 */
export function useTreatmentStats() {
  return useQuery({
    queryKey: ['treatmentStats'],
    queryFn: () => api.treatments.stats(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  })
}

/**
 * Hook to fetch treatment variations
 */
export function useTreatmentVariations(
  id: number | null,
  params?: { page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: ['treatment', id, 'variations', params],
    queryFn: () => api.treatments.variations(id!, params),
    enabled: id !== null && id > 0,
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook to fetch treatment invoices (linked invoices)
 */
export function useTreatmentInvoices(
  id: number | null,
  params?: { page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: ['treatment', id, 'invoices', params],
    queryFn: () => api.treatments.invoices(id!, params),
    enabled: id !== null && id > 0,
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook to fetch unmatched items
 */
export function useUnmatchedItems(params?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: ['treatmentsUnmatched', params],
    queryFn: () => api.treatments.unmatched(params),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook to create variation (map unmatched item to treatment)
 */
export function useCreateVariation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateVariationInput) => api.treatments.createVariation(data),

    onSuccess: () => {
      // Invalidate all treatment-related queries
      queryClient.invalidateQueries({ queryKey: ['treatments'] })
      queryClient.invalidateQueries({ queryKey: ['treatmentStats'] })
      queryClient.invalidateQueries({ queryKey: ['treatmentsUnmatched'] })
      queryClient.invalidateQueries({ queryKey: ['treatment'] })

      toast.success('Treatment mapped successfully')
    },

    onError: (error: Error) => {
      toast.error(error.message || 'Failed to map treatment')
    },
  })
}

/**
 * Hook to fetch treatment analytics summary
 */
export function useTreatmentAnalyticsSummary(branchId?: number) {
  return useQuery({
    queryKey: ['treatmentAnalyticsSummary', branchId],
    queryFn: () => api.treatments.analyticsSummary(branchId),
    staleTime: 60 * 1000,
  })
}

/**
 * Hook to fetch top treatments by revenue
 */
export function useTopTreatments(params?: { branchId?: number; limit?: number }) {
  return useQuery({
    queryKey: ['topTreatments', params],
    queryFn: () => api.treatments.analyticsTop(params),
    staleTime: 60 * 1000,
  })
}

/**
 * Hook to fetch revenue by category
 */
export function useTreatmentsByCategory(branchId?: number) {
  return useQuery({
    queryKey: ['treatmentsByCategory', branchId],
    queryFn: () => api.treatments.analyticsByCategory(branchId),
    staleTime: 60 * 1000,
  })
}
